import { useApolloClient } from "@apollo/client";
import { ModalActions, ModalContent, ModalDialog } from "@msys/ui";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useUserData } from "../../auth/useUserData.js";
import {
  WizardSection,
  applyItemActionToApplyItemActionInput,
} from "../quotes/WizardSection.js";
import { useRequirementItemCompletionWizardModal_ApplyQuoteItemActionsMutation } from "./RequirementItemCompletionWizardModal.generated";

type HandleClose = () => void;

export function RequirementItemCompletionWizardModal({
  projectId,
  requirementId,
  handleClose,
  itemId,
  refetchAfterApplyItemAction,
}: {
  projectId: string;
  requirementId: string;
  handleClose: HandleClose;
  itemId: string;
  refetchAfterApplyItemAction?: () => Promise<void>;
}) {
  const { t } = useTranslate(["Decisions", "Global"]);

  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [applyQuoteItemActionsMutation] =
    useRequirementItemCompletionWizardModal_ApplyQuoteItemActionsMutation({
      client,
    });

  const [isCompleted, setIsCompleted] = React.useState(false);

  const handleCloseWithRefetch = React.useCallback(async () => {
    client.reFetchObservableQueries();
    refetchAfterApplyItemAction?.();
    handleClose();
  }, [client, handleClose, refetchAfterApplyItemAction]);

  return (
    <ModalDialog maxWidth="lg">
      {isCompleted ? (
        <>
          <ModalContent>
            <Stack
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 140 }} />
              <Typography
                variant={"h2"}
                sx={{
                  color: "success.main",
                }}
              >
                {t("Your answers have been saved!", {
                  ns: "Decisions",
                })}
              </Typography>
              <Typography
                sx={{
                  textAlign: "center",
                }}
              >
                {t(
                  "Start the wizard again and you will be able to review any question that hasn't been finalised yet.",
                  { ns: "Decisions" }
                )}
              </Typography>
            </Stack>
          </ModalContent>
          <ModalActions
            actionButtons={[
              {
                label: t("Close", { ns: "Global" }),
                handleClick: handleCloseWithRefetch,
                buttonProps: { variant: "contained" },
              },
            ]}
          />
        </>
      ) : (
        <ModalContent>
          <WizardSection
            wizardConfiguration={{
              navigationType: null,
              questionStrategy: {
                type: "item",
                itemId,
              },
              requestInitialAnswers: "false",
              sourceDoc: {
                type: "requirement",
                requirementId,
                owningOrganisationId: viewer.organisation.id,
              },
              showCloseButton: "true",
            }}
            initialAnswers={[]}
            onAnswer={async answers => {
              const mostRecentAnswer = answers[answers.length - 1];

              await applyQuoteItemActionsMutation({
                variables: {
                  input: {
                    applyItemActions: [
                      applyItemActionToApplyItemActionInput(
                        mostRecentAnswer.answer
                      ),
                    ],
                    projectId,
                    requirementId,
                  },
                },
              });
            }}
            onComplete={async result => {
              setIsCompleted(true);
            }}
            onClose={handleCloseWithRefetch}
          />
        </ModalContent>
      )}
    </ModalDialog>
  );
}
